import { token } from '../../theme';
import { CanvasComponent } from '../canvas-base';

export type SymbolKind =
  | 'preamble'
  | 'sync'
  | 'data'
  | 'end'
  | 'listening'
  | 'detected';

interface StreamCell {
  symbol: number;
  kind: SymbolKind;
}

/**
 * Symbol stream — equipment-style 4-FSK symbol log.
 *
 * Two stacked rows in one canvas:
 *   1. STRIP — uniform-height colored ticks per symbol (color = trace token)
 *   2. HEX   — symbol value (0..3) in mono digits, tabular alignment
 *
 * Section transitions are marked by single-pixel vertical hairlines plus
 * a 3-char tag at the top edge ("PRE", "SYN", "DAT", "END") — no floating labels.
 */
export class SymbolStream extends CanvasComponent {
  private cells: StreamCell[] = [];
  private maxCells = 320;

  private numSymbols = 4;

  private colSurface = '#0a0e16';
  private colBorder = '#1a2332';
  private colDivider = 'rgba(120,180,240,0.32)';
  private colDim = '#7e8a9a';
  private colMute = '#3e4858';
  private colTextStrong = '#f0f3f7';
  private traceColors: string[] = ['#00d9ff', '#a3ff00', '#ffb000', '#ff3a8c'];

  constructor() {
    super();
    this.el.classList.add('symbol-stream');
    this.attachLifecycle();
    this.cacheColors();
  }

  pushSymbol(symbol: number, kind: SymbolKind = 'data'): void {
    this.cells.push({ symbol, kind });
    if (this.cells.length > this.maxCells) {
      this.cells.splice(0, this.cells.length - this.maxCells);
    }
  }

  clear(): void {
    this.cells = [];
  }

  setSymbolCount(n: number): void {
    this.numSymbols = Math.max(2, n);
  }

  protected override onThemeChanged(): void {
    this.cacheColors();
  }

  private cacheColors(): void {
    this.colSurface = token('display-bg');
    this.colBorder = token('border-hairline');
    this.colDivider = token('graticule-axis', 'rgba(120,180,240,0.32)');
    this.colDim = token('text-dim');
    this.colMute = token('text-mute');
    this.colTextStrong = token('text-strong');
    this.traceColors = [
      token('trace-f0'),
      token('trace-f1'),
      token('trace-f2'),
      token('trace-f3'),
    ];
  }

  protected draw(): void {
    if (this.w === 0 || this.h === 0) return;
    const { ctx, w, h } = this;

    ctx.fillStyle = this.colSurface;
    ctx.fillRect(0, 0, w, h);

    // Layout
    const tagH = 12;
    const hexH = Math.max(12, Math.min(16, Math.floor(h * 0.22)));
    const stripeY = tagH;
    const stripeH = h - tagH - hexH - 1;
    const hexY = stripeY + stripeH + 1;

    // Background tint for stripe band
    ctx.fillStyle = token('display-bg');
    ctx.fillRect(0, stripeY, w, stripeH);

    this.drawCells(stripeY, stripeH, hexY, hexH);
    this.drawCornerTags(tagH);
    this.drawBorder();
  }

  private drawCells(
    stripeY: number,
    stripeH: number,
    hexY: number,
    hexH: number,
  ): void {
    const { ctx, w, cells } = this;
    if (cells.length === 0) return;

    const visible = cells;
    const cellW = Math.max(2, w / Math.max(visible.length, this.maxCells / 2));

    ctx.font = `${Math.max(8, hexH - 4)}px ${token('font-mono', 'monospace')}`;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';

    let lastKind: SymbolKind | null = null;
    for (let i = 0; i < visible.length; i++) {
      const cell = visible[i];
      const x = w - (visible.length - i) * cellW;
      if (x + cellW < 0) continue;

      // Section divider
      if (lastKind !== null && lastKind !== cell.kind) {
        ctx.strokeStyle = this.colDivider;
        ctx.lineWidth = 1;
        ctx.globalAlpha = 0.7;
        ctx.beginPath();
        ctx.moveTo(Math.round(x) + 0.5, 0);
        ctx.lineTo(Math.round(x) + 0.5, this.h);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }

      // Stripe — uniform height, full color rectangle
      const sym = Math.max(0, Math.min(this.numSymbols - 1, cell.symbol));
      const color = this.traceColors[sym] ?? this.colDim;

      // Tone by kind
      let alpha = 1;
      switch (cell.kind) {
        case 'listening':
          alpha = 0.18;
          break;
        case 'preamble':
          alpha = 0.55;
          break;
        case 'sync':
          alpha = 0.7;
          break;
        case 'end':
          alpha = 0.65;
          break;
        case 'data':
        case 'detected':
        default:
          alpha = 1;
      }
      ctx.globalAlpha = alpha;
      ctx.fillStyle = color;
      ctx.fillRect(
        Math.round(x),
        Math.round(stripeY),
        Math.max(1, Math.floor(cellW)),
        Math.round(stripeH),
      );
      ctx.globalAlpha = 1;

      // Hex value (only when cell is wide enough to be readable)
      if (cellW >= 8 && cell.kind !== 'listening') {
        ctx.fillStyle = this.colMute;
        ctx.fillText(
          String(sym),
          Math.round(x + cellW / 2),
          Math.round(hexY + hexH / 2),
        );
      }

      lastKind = cell.kind;
    }
  }

  private drawCornerTags(tagH: number): void {
    // Show the kind of the most recent cell as a tiny corner tag.
    const last = this.cells[this.cells.length - 1];
    const { ctx, w } = this;
    ctx.font = `bold 9px ${token('font-mono', 'monospace')}`;
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';

    ctx.fillStyle = this.colMute;
    ctx.fillText('SYM', 6, 2);

    if (last) {
      ctx.fillStyle = this.colTextStrong;
      const tag = this.kindShortLabel(last.kind);
      ctx.textAlign = 'right';
      ctx.fillText(tag, w - 6, 2);
    }
    void tagH;
  }

  private drawBorder(): void {
    const { ctx, w, h } = this;
    ctx.strokeStyle = this.colBorder;
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, w - 1, h - 1);
  }

  private kindShortLabel(kind: SymbolKind): string {
    switch (kind) {
      case 'preamble':
        return 'PRE';
      case 'sync':
        return 'SYN';
      case 'data':
        return 'DAT';
      case 'end':
        return 'END';
      case 'detected':
        return 'RX';
      case 'listening':
        return 'LSN';
    }
  }
}
